import { useAuth } from '@/contexts'
import { City, IPersonalDetails, IPreferences, ISlot, MeetingType } from '@namuho/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

export const usePreferences = () => {
    const baseUrl = `${import.meta.env.VITE_API_URL}/api/users/`
    const queryClient = useQueryClient()

    const { session, user, getAccessToken } = useAuth()
    const userData = useQuery({
        queryKey: [user?.id],
        queryFn: async () => {
            const response = await fetch(`${baseUrl}${user?.id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: (await getAccessToken()) || '',
                },
            })

            if (!response.ok) {
                throw new Error(response.statusText)
            }

            return response.json()
        },
        enabled: !!user?.id && !!session,
    })
    const updateFriendlyPreferences = useMutation({
        mutationKey: [user?.id],
        mutationFn: async (friendlyPreferences: IPreferences) => {
            const updateObject = {
                [MeetingType.Friendly]: friendlyPreferences,
            }
            const response = await fetch(`${baseUrl}${user?.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: (await getAccessToken()) || '',
                },
                body: JSON.stringify(updateObject),
            })

            if (!response.ok) {
                throw new Error('Failed to update User')
            }

            return response.json()
        },
        onSuccess: (data) => {
            queryClient.setQueryData([user?.id], data)
        },
        onError: () => {
            toast.error('Etwas ist schiefgelaufen')
        },
    })

    const updateRomanticPreferences = useMutation({
        mutationKey: [user?.id],
        mutationFn: async (romanticPreferences: IPreferences) => {
            const updateObject = {
                [MeetingType.Romantic]: romanticPreferences,
            }
            const response = await fetch(`${baseUrl}${user?.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: (await getAccessToken()) || '',
                },
                body: JSON.stringify(updateObject),
            })

            if (!response.ok) {
                throw new Error('Failed to update User')
            }

            return response.json()
        },
        onSuccess: (data) => {
            queryClient.setQueryData([user?.id], data)
        },
        onError: () => {
            toast.error('Etwas ist schiefgelaufen')
        },
    })
    const updateGroupPreferenes = useMutation({
        mutationKey: [user?.id],
        mutationFn: async (groupPreferences: IPreferences) => {
            const updateObject = {
                [MeetingType.Group]: groupPreferences,
            }
            const response = await fetch(`${baseUrl}${user?.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: (await getAccessToken()) || '',
                },
                body: JSON.stringify(updateObject),
            })

            if (!response.ok) {
                throw new Error('Failed to update User')
            }

            return response.json()
        },
        onSuccess: (data) => {
            queryClient.setQueryData([user?.id], data)
        },
        onError: () => {
            toast.error('Etwas ist schiefgelaufen')
        },
    })
    const updatePersonalDetails = useMutation({
        mutationKey: [user?.id],
        mutationFn: async (preferences: IPersonalDetails) => {
            const updateObject = {
                personalDetails: preferences,
            }
            const response = await fetch(`${baseUrl}${user?.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: (await getAccessToken()) || '',
                },
                body: JSON.stringify(updateObject),
            })

            if (!response.ok) {
                throw new Error('Failed to update User')
            }

            return response.json()
        },
        onSuccess: (data) => {
            queryClient.setQueryData([user?.id], data)
        },
        onError: () => {
            toast.error('Etwas ist schiefgelaufen')
        },
    })
    const updateTimeSlots = useMutation({
        mutationKey: [user?.id],
        mutationFn: async (timeSlots: ISlot[]) => {
            const updateObject = {
                timeSlots,
            }
            const response = await fetch(`${baseUrl}${user?.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: (await getAccessToken()) || '',
                },
                body: JSON.stringify(updateObject),
            })

            if (!response.ok) {
                throw new Error('Failed to update User Time slots')
            }

            return response.json()
        },
        onSuccess: (data) => {
            queryClient.setQueryData([user?.id], data)
        },
        onError: () => {
            toast.error('Etwas ist schiefgelaufen')
        },
    })
    const updateCity = useMutation({
        mutationKey: [user?.id],
        mutationFn: async (city: City) => {
            const updateObject = {
                city,
            }
            const response = await fetch(`${baseUrl}${user?.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: (await getAccessToken()) || '',
                },
                body: JSON.stringify(updateObject),
            })

            if (!response.ok) {
                throw new Error('Failed to update City')
            }

            return response.json()
        },
        onSuccess: (data) => {
            queryClient.setQueryData([user?.id], data)
        },
        onError: () => {
            toast.error('Etwas ist schiefgelaufen')
        },
    })

    return {
        isLoading: userData.isLoading,
        personalDetails: userData.data?.personalDetails,
        friendlyPreferences: userData.data?.[MeetingType.Friendly],
        romanticPreferences: userData.data?.[MeetingType.Romantic],
        groupPreferences: userData.data?.[MeetingType.Group],
        updateFriendlyPreferences: updateFriendlyPreferences.mutateAsync,
        updateRomanticPreferences: updateRomanticPreferences.mutateAsync,
        updateGroupPreferences: updateGroupPreferenes.mutateAsync,
        updatePersonalDetails: updatePersonalDetails.mutateAsync,
        updateCity: updateCity.mutateAsync,
        updateTimeSlots: updateTimeSlots.mutateAsync,
        updateTimeSlotsLoading: updateTimeSlots.isLoading,
    }
}
